import logging from "utils/logging";
import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGameId,
  selectGameStatusObject,
} from "../../gamestate/gameStateSlice";
import {
  selectPlayerSubmissionsForGameboardLeaderboard,
  stopWatchingQuestionSubmissionsAction,
  watchQuestionSubmissionsAction,
} from "../../question/submissionSlice";
import {
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
} from "../../gamestate/constants";

const log = logging.getLogger("Gameboard Leaderboard");

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';

const ScoreboardContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(1),
  background: `linear-gradient(120deg, ${ACCENT_TEAL}ee 0%, ${ACCENT_YELLOW}ee 100%)`,
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1.5),
  maxHeight: "calc(100vh - 16px)",
  overflowY: "auto",
  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  color: TEXT_COLOR,
  margin: theme.spacing(1),
}));

const ScoreTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.4rem',
  marginBottom: theme.spacing(0.75),
  textAlign: "center",
  letterSpacing: "0.02em",
}));

const ScoreRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(0.5, 1),
  margin: theme.spacing(0.4, 0),
  borderRadius: theme.spacing(1),
  background: "rgba(255,255,255,0.85)",
}));

const PlayerName = styled(Typography)({
  fontWeight: 600,
  fontSize: '1.1rem',
  display: 'flex',
  alignItems: 'center',
});

const PlayerScore = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.2rem',
});

const PlusPoints = styled('span')({
  color: "#1bbc1b",
  fontSize: "0.75em",
});

const MinusPoints = styled('span')({
  color: "#bc1b1b",
  fontSize: "0.75em",
});

const GameboardLeaderboardRow = ({
  player,
  gameState,
  index,
  isHighlighted,
}) => {
  let scoreChangeText = <span />;
  const showScoreChange = gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS;
  if (player.submission && showScoreChange) {
    log.info("PLayer has sub: ", player);
    const positive = player.submission.points >= 0;
    const sign = positive ? " +" : " ";
    scoreChangeText = positive ? (
      <PlusPoints>{sign + player.submission.points.toLocaleString()}</PlusPoints>
    ) : (
      <MinusPoints>{sign + player.submission.points.toLocaleString()}</MinusPoints>
    );
  }

  return (
    <ScoreRow key={player.id}>
      <PlayerName>
        {isHighlighted ? "→ " : ""}{player.name}{scoreChangeText}
      </PlayerName>
      <PlayerScore>
        {player.score >= 0 ? 
          "$" + player.score.toLocaleString() :
          "- $" + Math.abs(player.score).toLocaleString()}
      </PlayerScore>
    </ScoreRow>
  );
};

const GameboardLeaderboard = () => {
  const dispatch = useDispatch();

  // const players = useSelector(selectLeaderboardPlayers);
  const players = useSelector(selectPlayerSubmissionsForGameboardLeaderboard);
  const gameStateObj = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);
  const questionId = gameStateObj.questionId;

  useEffect(() => {
    if (gameId && questionId) {
      dispatch(watchQuestionSubmissionsAction(gameId, questionId));
    }

    return function cleanup() {
      if (gameId && questionId) {
        dispatch(stopWatchingQuestionSubmissionsAction(gameId, questionId));
      }
    };
  }, [dispatch, questionId, gameId]);

  let list;
  if (!players) {
    list = <Typography>Loading...</Typography>;
  } else {
    list = players.map((player, idx) => (
      
      <GameboardLeaderboardRow
        player={player}
        gameState={gameStateObj}
        key={`player-${player.id}`}
        index={idx}
        isHighlighted={gameStateObj.playerInControl === player.id}
      />
    ));
  }

  let headerText = "Leaderboard";

  return (
    <ScoreboardContainer>
      <ScoreTitle>{headerText}</ScoreTitle>
      {list}
    </ScoreboardContainer>
  );
};

export default GameboardLeaderboard;
